import type { BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/query/react';
import { buildCreateApi, coreModule, fetchBaseQuery, reactHooksModule, retry } from '@reduxjs/toolkit/query/react';
import emptyObject from 'fbjs/lib/emptyObject';
import cookies from 'js-cookie';
import { tags } from './tags';
import type { ApiError } from './types';

const passthroughHeaders = [
  'cookie',
  'accept-language',
  'user-agent',
  'x-forwarded-for',
  'host',
  'x-forwarded-host',
  'x-request-id',
];

// @ts-ignore cannot override types from fetchBaseQuery, despite this being okay
const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  ApiError,
  Record<string, unknown>,
  Record<string, unknown>
> = fetchBaseQuery({
  baseUrl: `${window.location.origin}/`,
  prepareHeaders: (headers: Headers, { getState }) => {
    headers.set('accept', 'application/json');

    const state = ((typeof getState === 'function' && getState()) || emptyObject) as {
      requestHeaders?: { headers: Record<string, string> };
      api: { queries: Record<string, { data: Record<string, unknown> }> };
    };

    const csrftoken = cookies.get('csrftoken');
    if (csrftoken) {
      headers.set('x-csrftoken', csrftoken);
    }

    const otherHeaders = state.requestHeaders?.headers;
    if (otherHeaders) {
      passthroughHeaders.forEach((header) => {
        headers.set(header, otherHeaders[header]);
      });
    }
    return headers;
  },
});

const retryableBaseQuery = retry(
  async function retryable(args, api, extraOptions) {
    const result = await baseQuery(args, api, extraOptions);

    const failStatus = [401, 400, 429];
    if (failStatus.some((status) => result.error?.status === status)) {
      retry.fail(result.error);
    }

    return result;
  },
  { maxRetries: 5 }
);

const createApi = buildCreateApi(coreModule(), reactHooksModule());

export const api = createApi({
  baseQuery: retryableBaseQuery,
  endpoints: () => ({}),
  refetchOnFocus: true,
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: 900,
  keepUnusedDataFor: 60,
  tagTypes: Object.values(tags),
});
